import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, LineChart as LineChartIcon, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  PortalMetric,
  PortalMetricCard,
  PortalMetricSeries,
  PortalSeriesParams,
  portalAdminService,
} from '../../services/api';

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

/** میانگین، مجموع یا میانه — کدام عدد نقطهٔ نمودار گروه باشد */
type Aggregate = 'avg' | 'sum' | 'median';
const AGGREGATE_LABELS: Record<Aggregate, string> = {
  avg: 'میانگین',
  sum: 'مجموع',
  median: 'میانه',
};

/**
 * قالب‌بندی عدد بر اساس فرمت شاخص.
 * ارقام فارسی می‌شوند تا با بقیهٔ پنل هم‌خوان باشد.
 */
const formatValue = (value: number | null, metric?: PortalMetric) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 100) / 100;
  const base = rounded.toLocaleString('fa-IR');
  switch (metric?.format) {
    case 'CURRENCY':
      return base + ' ' + (metric.unit || 'تومان');
    case 'PERCENT':
      return base + '٪';
    case 'DURATION':
      return base + ' ' + (metric.unit || 'دقیقه');
    default:
      return metric?.unit ? base + ' ' + metric.unit : base;
  }
};

/**
 * آیا این تغییر خوب است؟ به جهت شاخص بستگی دارد —
 * برای «هزینه» کاهش خوب است و برای «فروش» افزایش.
 */
const changeTone = (change: number | null, metric?: PortalMetric) => {
  if (change === null || change === 0 || metric?.direction === 'NEUTRAL') return 'neutral';
  const good = metric?.direction === 'DOWN' ? change < 0 : change > 0;
  return good ? 'good' : 'bad';
};

const TONE_CLASS: Record<string, string> = {
  good: 'text-emerald-600',
  bad: 'text-red-600',
  neutral: 'text-gray-500',
};

interface Props {
  programId: string;
  cohorts: any[];
  onError: (err: any, fallback: string) => void;
}

/**
 * تب تحلیل — روند شاخص‌های عددی در طول دوره.
 *
 * محور افقی «تسک» است نه هفته: تسک‌ها خودشان رویدادهای جمع‌آوری‌اند و سطل
 * زمانی ساختگی، بازه‌های خالی می‌سازد.
 */
export const PortalAnalyticsTab: React.FC<Props> = ({ programId, cohorts, onError }) => {
  const [metrics, setMetrics] = useState<PortalMetric[]>([]);
  const [cards, setCards] = useState<PortalMetricCard[]>([]);
  const [series, setSeries] = useState<PortalMetricSeries | null>(null);

  const [metricId, setMetricId] = useState('');
  const [cohortId, setCohortId] = useState('');
  const [onlyApproved, setOnlyApproved] = useState(false);
  const [aggregate, setAggregate] = useState<Aggregate>('avg');
  const [loading, setLoading] = useState(false);

  const params = useMemo<PortalSeriesParams | null>(
    () => (metricId ? { metricId, cohortId: cohortId || undefined, onlyApproved } : null),
    [metricId, cohortId, onlyApproved],
  );

  const loadMetrics = useCallback(async () => {
    try {
      const list = await portalAdminService.listMetrics(programId);
      setMetrics(list);
      // اولین شاخص فعال به‌صورت پیش‌فرض انتخاب می‌شود تا صفحه خالی باز نشود
      setMetricId((current) => current || list.find((m) => m.isActive)?.id || '');
    } catch (err: any) {
      onError(err, 'دریافت شاخص‌ها انجام نشد.');
    }
  }, [programId, onError]);

  const loadCards = useCallback(async () => {
    try {
      setCards(await portalAdminService.getMetricSummary(programId, onlyApproved));
    } catch (err: any) {
      onError(err, 'دریافت کارت شاخص‌ها انجام نشد.');
    }
  }, [programId, onlyApproved, onError]);

  const loadSeries = useCallback(async () => {
    if (!params) {
      setSeries(null);
      return;
    }
    setLoading(true);
    try {
      setSeries(await portalAdminService.getMetricSeries(params));
    } catch (err: any) {
      onError(err, 'دریافت روند شاخص انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [params, onError]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);
  useEffect(() => {
    loadCards();
  }, [loadCards]);
  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const selectedMetric = series?.metric;

  const chartData = useMemo(
    () =>
      (series?.points ?? []).map((p) => ({
        name: p.title.length > 18 ? p.title.slice(0, 18) + '…' : p.title,
        value: p[aggregate],
        count: p.count,
      })),
    [series, aggregate],
  );

  /** مقایسهٔ گروه‌ها فقط وقتی معنا دارد که روی یک گروه فیلتر نشده باشیم */
  const cohortComparison = useMemo(() => {
    if (!series || cohortId) return [];
    const byCohort = new Map<string, number[]>();
    for (const p of series.participants) {
      if (p.latest === null) continue;
      const list = byCohort.get(p.cohort) ?? [];
      list.push(p.latest);
      byCohort.set(p.cohort, list);
    }
    return Array.from(byCohort.entries())
      .map(([name, values]) => ({
        name,
        value: Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100,
        count: values.length,
      }))
      .sort((a, b) => b.value - a.value);
  }, [series, cohortId]);

  const exportCsv = async () => {
    if (!params) return;
    try {
      const blob = await portalAdminService.downloadMetricCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'metric-' + (selectedMetric?.key || 'series') + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      onError(err, 'دریافت خروجی CSV انجام نشد.');
    }
  };

  return (
    <div className="space-y-4">
      {/* کارت شاخص‌های سنجاق‌شده */}
      {cards.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card) => {
            const tone = changeTone(card.change, card.metric);
            const Icon =
              tone === 'good' ? TrendingUp : tone === 'bad' ? TrendingDown : Minus;
            return (
              <button
                key={card.metric.id}
                type="button"
                onClick={() => setMetricId(card.metric.id)}
                className={
                  'rounded-xl border bg-white p-3 text-right transition hover:border-blue-300 ' +
                  (metricId === card.metric.id ? 'border-blue-400' : 'border-gray-200')
                }
              >
                <p className="mb-1 truncate text-xs font-bold text-gray-500">
                  {card.metric.title}
                </p>
                <p className="text-lg font-black text-gray-900">
                  {formatValue(card.current, card.metric)}
                </p>
                <p className={'mt-1 flex items-center gap-1 text-xs font-bold ' + TONE_CLASS[tone]}>
                  <Icon className="h-3.5 w-3.5" />
                  {card.changePercent !== null
                    ? Math.abs(card.changePercent).toLocaleString('fa-IR') + '٪ نسبت به قبل'
                    : 'بدون مقایسه'}
                </p>
              </button>
            );
          })}
        </div>
      ) : null}

      {/* فیلترها */}
      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="grid gap-3 md:grid-cols-4">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">شاخص</span>
            <select
              value={metricId}
              onChange={(e) => setMetricId(e.target.value)}
              className={inputClass}
            >
              <option value="">— انتخاب کنید —</option>
              {metrics
                .filter((m) => m.isActive)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">گروه</span>
            <select
              value={cohortId}
              onChange={(e) => setCohortId(e.target.value)}
              className={inputClass}
            >
              <option value="">همهٔ گروه‌ها</option>
              {cohorts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-bold text-gray-600">نقطهٔ نمودار</span>
            <select
              value={aggregate}
              onChange={(e) => setAggregate(e.target.value as Aggregate)}
              className={inputClass}
            >
              {(Object.keys(AGGREGATE_LABELS) as Aggregate[]).map((a) => (
                <option key={a} value={a}>
                  {AGGREGATE_LABELS[a]}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-end gap-2">
            <label className="flex flex-1 items-center gap-1.5 text-xs font-bold text-gray-700">
              <input
                type="checkbox"
                checked={onlyApproved}
                onChange={(e) => setOnlyApproved(e.target.checked)}
              />
              فقط تأییدشده‌ها
            </label>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!metricId}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
          </div>
        </div>
      </div>

      {!metricId ? (
        <EmptyState
          title="هنوز شاخصی انتخاب نشده"
          body="یک شاخص از فهرست بالا انتخاب کنید تا روندش را ببینید. اگر فهرست خالی است، از تب «تسک‌ها» روی فیلدهای عددی شاخص بسازید."
        />
      ) : loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          در حال محاسبه…
        </div>
      ) : !series || series.points.length === 0 ? (
        <EmptyState
          title="داده‌ای برای این شاخص ثبت نشده"
          body="وقتی شرکت‌کنندگان تسکی را که فیلدش به این شاخص وصل است ارسال کنند، نمودار اینجا ساخته می‌شود."
        />
      ) : (
        <>
          {/* روند */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <LineChartIcon className="h-4 w-4 text-blue-500" />
              <p className="text-sm font-bold text-gray-800">
                روند {AGGREGATE_LABELS[aggregate]} · {selectedMetric?.title}
              </p>
            </div>
            <div className="h-72 w-full" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={70} />
                  <Tooltip
                    formatter={(v: any) => formatValue(Number(v), selectedMetric)}
                    labelStyle={{ direction: 'rtl' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={selectedMetric?.title}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* مقایسهٔ گروه‌ها */}
          {cohortComparison.length > 1 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="mb-3 text-sm font-bold text-gray-800">
                مقایسهٔ گروه‌ها — آخرین عدد ثبت‌شدهٔ هر نفر
              </p>
              <div className="h-56 w-full" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cohortComparison} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={70} />
                    <Tooltip formatter={(v: any) => formatValue(Number(v), selectedMetric)} />
                    <Legend />
                    <Bar dataKey="value" fill="#6366f1" name="میانگین گروه" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          {/* رتبه‌بندی */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full min-w-[640px] text-right text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-bold">شرکت‌کننده</th>
                  <th className="px-3 py-2 font-bold">گروه</th>
                  <th className="px-3 py-2 font-bold">آخرین عدد</th>
                  <th className="px-3 py-2 font-bold">تغییر از ابتدا</th>
                  <th className="px-3 py-2 font-bold">تعداد ثبت</th>
                </tr>
              </thead>
              <tbody>
                {series.participants.map((p) => {
                  const tone = changeTone(p.change, selectedMetric);
                  return (
                    <tr key={p.participantId} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-bold text-gray-800">{p.name}</td>
                      <td className="px-3 py-2 text-gray-600">{p.cohort}</td>
                      <td className="px-3 py-2 text-gray-900">
                        {formatValue(p.latest, selectedMetric)}
                      </td>
                      <td className={'px-3 py-2 font-bold ' + TONE_CLASS[tone]}>
                        {p.changePercent !== null
                          ? (p.changePercent > 0 ? '+' : '') +
                            p.changePercent.toLocaleString('fa-IR') +
                            '٪'
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {p.values
                          .filter((v) => v !== null)
                          .length.toLocaleString('fa-IR')}{' '}
                        از {series.points.length.toLocaleString('fa-IR')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const EmptyState: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
    <p className="mb-1 text-sm font-bold text-gray-700">{title}</p>
    <p className="mx-auto max-w-md text-xs leading-6 text-gray-500">{body}</p>
  </div>
);

export default PortalAnalyticsTab;
